import { Avatar, Input, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { FC } from 'react';
import { useSelector } from 'react-redux';

import type { HeaderBlockProps } from './types';
import type { TemplateNavigationLink } from '../system/templateTypes';
import { RootState, useAppDispatch } from '@store/store.tsx';
import { setSearchPrompt } from '@store/slices/eshopSlice.tsx';

const { Title } = Typography;

interface HeaderDesignConfig {
  avatarSize: number;
  headerClassName: string;
  navigationWrapperClassName: string;
  navigationLinkClassName: (link: TemplateNavigationLink, isActive: boolean) => string;
  navigationContainerRole: string;
  navigationAriaLabel: string;
}

const HEADER_DESIGN_CONFIG: Record<string, HeaderDesignConfig> = {
  minimalista: {
    avatarSize: 52,
    headerClassName: 'catalogue-header catalogue-header--minimalista',
    navigationWrapperClassName: 'catalogue-header__nav',
    navigationLinkClassName: (_link, isActive) =>
      `catalogue-header__nav-link${isActive ? ' is-active' : ''}`,
    navigationContainerRole: 'navigation',
    navigationAriaLabel: 'Secciones',
  },
  moderno: {
    avatarSize: 56,
    headerClassName: '',
    navigationWrapperClassName: 'shop-navbar',
    navigationLinkClassName: (_link, isActive) =>
      `shop-navbar__link${isActive ? ' is-active' : ''}`,
    navigationContainerRole: 'navigation',
    navigationAriaLabel: 'Navegación principal del catálogo',
  },
};

const getHeaderConfig = (design: string | undefined): HeaderDesignConfig => {
  const base = HEADER_DESIGN_CONFIG[design ?? ''];
  return (
    base ?? {
      avatarSize: 56,
      headerClassName: '',
      navigationWrapperClassName: 'shop-navbar',
      navigationLinkClassName: (_link, isActive) =>
        `shop-navbar__link${isActive ? ' is-active' : ''}`,
      navigationContainerRole: 'navigation',
      navigationAriaLabel: 'Navegación principal',
    }
  );
};

export const BrandBlock: FC<HeaderBlockProps> = ({ brand, design }) => {
  const config = getHeaderConfig(design);

  return (
    <a
      href={brand.homeUrl}
      className="eshop-header__brand"
      aria-label={`Ir al catálogo de ${brand.name}`}
    >
      {brand.logoUrl ? (
        <img src={brand.logoUrl} alt={brand.logoAlt ?? brand.name} className="eshop-header__logo" />
      ) : (
        <Avatar className="eshop-header__logo" size={config.avatarSize}>
          {brand.name.charAt(0)}
        </Avatar>
      )}

      <div className="eshop-header__brand-text">
        <Title level={4} className="eshop-header__title mb-0">
          {brand.name}
        </Title>
        {/*{brand.description && (*/}
        {/*  <Text className="eshop-header__subtitle" ellipsis>*/}
        {/*    {brand.description}*/}
        {/*  </Text>*/}
        {/*)}*/}
      </div>
    </a>
  );
};

const renderNavigationLinks = (
  links: TemplateNavigationLink[] | undefined,
  activeKey: string | undefined,
  config: HeaderDesignConfig,
) => {
  if (!links?.length) {
    return null;
  }

  return (
    <Space align="center" size={config.navigationWrapperClassName === 'shop-navbar' ? 16 : 24}>
      {links.map((link) => {
        const isActive = link.key === activeKey;
        const className = config.navigationLinkClassName(link, isActive);
        return (
          <a
            key={link.key}
            href={link.href}
            className={className}
            target={link.external ? '_blank' : undefined}
            rel={link.external ? 'noreferrer noopener' : undefined}
          >
            {link.label}
            {config.navigationWrapperClassName === 'shop-navbar' &&
              typeof link.badgeCount === 'number' &&
              link.badgeCount > 0 && (
                <span className="shop-navbar__badge" data-count={link.badgeCount} />
              )}
          </a>
        );
      })}
    </Space>
  );
};

export const NavigationBlock: FC<HeaderBlockProps> = ({
  slots,
  navigationLinks,
  activeNavigationKey,
  design,
}) => {
  const config = getHeaderConfig(design);

  if (slots?.navigation) {
    return <>{slots.navigation}</>;
  }

  const navigation = renderNavigationLinks(navigationLinks, activeNavigationKey, config);
  if (!navigation) {
    return null;
  }

  return (
    <nav
      className={config.navigationWrapperClassName}
      aria-label={config.navigationAriaLabel}
      role={config.navigationContainerRole}
    >
      {navigation}
    </nav>
  );
};

export const ActionsBlock: FC<HeaderBlockProps> = ({ slots }) => {
  if (!slots?.actions) {
    return null;
  }

  return <>{slots.actions}</>;
};

export const ConfiguratorBlock: FC<HeaderBlockProps> = ({ slots }) => {
  if (!slots?.configurator) {
    return null;
  }

  return <div className="eshop-header__config">{slots.configurator}</div>;
};

export const HeaderSearchBlock: FC<HeaderBlockProps> = () => {
  const dispatch = useAppDispatch();
  const value = useSelector((state: RootState) => state.eshop.data.searchPrompt);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawVal = event.target.value;
    dispatch(setSearchPrompt(rawVal));
  };

  return (
    <Input
      className="catalogue-header-search"
      prefix={<SearchOutlined />}
      placeholder="Buscar productos..."
      value={value ?? ''}
      allowClear
      onChange={handleChange}
      aria-label="Buscar productos"
    />
  );
};

